'use client'

import React from 'react'
import { Badge } from '@/components/ui/badge'
import { Leaf, FlaskConical } from 'lucide-react'
import type { TerpeneRow } from '@/types/supabase'

interface TerpeneListItemProps {
  terpene: TerpeneRow
  isSelected: boolean
  onSelect: (terpene: TerpeneRow) => void
}

export default function TerpeneListItem({ 
  terpene, 
  isSelected, 
  onSelect 
}: TerpeneListItemProps) {
  return (
    <div
      onClick={() => onSelect(terpene)}
      className={`
        relative p-3 sm:p-4 rounded-lg cursor-pointer transition-all duration-200
        ${isSelected 
          ? 'bg-emerald-500/20 border border-emerald-400/50 shadow-lg shadow-emerald-500/20' 
          : 'bg-gray-800/30 border border-white/10 hover:bg-gray-800/50 hover:border-white/20'
        }
      `}
    >
      <div className="flex items-start justify-between gap-3">
        {/* Left side - Terpene info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Leaf className="h-4 w-4 text-emerald-400 flex-shrink-0" />
            <h4 className="font-semibold text-white truncate">
              {terpene.name}
            </h4>
          </div>
          
          {/* Category */}
          {terpene.category && (
            <div className="flex items-center gap-2 mb-2">
              <FlaskConical className="h-3 w-3 text-white/40" />
              <span className="text-xs text-white/60">{terpene.category}</span>
            </div>
          )}
          
          {/* Primary Effects - Show first 2-3 */}
          {terpene.primary_effects && terpene.primary_effects.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {terpene.primary_effects.slice(0, 2).map((effect, index) => (
                <Badge 
                  key={index}
                  variant="secondary"
                  className="text-xs bg-emerald-500/10 text-emerald-300 border-emerald-500/20 px-2 py-0"
                >
                  {effect}
                </Badge>
              ))}
              {terpene.primary_effects.length > 2 && (
                <span className="text-xs text-white/40">
                  +{terpene.primary_effects.length - 2}
                </span>
              )}
            </div>
          )}
          
          {/* Found in - Show first 2-3 sources */}
          {terpene.natural_sources && terpene.natural_sources.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              <span className="text-xs text-white/50 mr-1">Found in:</span>
              {terpene.natural_sources.slice(0, 2).map((source, index) => (
                <Badge 
                  key={index}
                  variant="outline"
                  className="text-xs bg-green-500/5 text-green-300/80 border-green-500/20 px-2 py-0"
                >
                  {source}
                </Badge>
              ))}
              {terpene.natural_sources.length > 2 && (
                <span className="text-xs text-white/40">
                  +{terpene.natural_sources.length - 2}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Right side - Boiling point */}
        <div className="text-right flex-shrink-0">
          {terpene.boiling_point_c && (
            <div className="text-xs text-white/60">
              {terpene.boiling_point_c}°C
            </div>
          )}
        </div>
      </div>

      {/* Selected indicator */}
      {isSelected && (
        <div className="absolute inset-0 rounded-lg ring-2 ring-emerald-400/30 pointer-events-none" />
      )}
    </div>
  )
}